import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button';
import type { AllTableCriteria } from '@openx/types/tableCriteria';
import { createRef, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFiltersContext } from '../../../context/FiltersContext';
import type { FiltersContext } from '../../../context/types';
import { FILTER_TYPES, type FilterCriteria, type FilterValue } from '../../types';
import type { AutocompleteSelectorRef } from '../filterContent/AutocompleteSelector';
import { FilterContentRenderer } from '../filterContent/FilterContentRenderer';

type MoreFiltersMenuProps<Criteria extends FilterCriteria> = {
  anchorEl: HTMLElement | null;
  open: boolean;
  filters: FiltersContext<Criteria>['filtersConfig'];
  criteria: AllTableCriteria<Criteria>;
  onClose: () => void;
  onFilterClear: () => void;
  onFilterApply: () => void;
  onFilterChange: (key: string, value: FilterValue) => void;
  onFilterChangeImmediately: (key: string, value: FilterValue) => void;
};

export const MoreFiltersMenu = <Criteria extends Record<string, FilterValue>>({
  anchorEl,
  open,
  filters,
  criteria,
  onClose,
  onFilterClear,
  onFilterApply,
  onFilterChange,
  onFilterChangeImmediately,
}: MoreFiltersMenuProps<Criteria>) => {
  const { t } = useTranslation();
  const [isApplyButtonDisabled, setIsApplyButtonDisabled] = useState(false);

  const { loadingFilters, disabledFilters } = useFiltersContext<Criteria>();

  const isInnerPopoverOpenRef = useRef(false);
  const autocompleteRefs = useRef<Record<string, React.RefObject<AutocompleteSelectorRef | null>>>({});

  filters.forEach(filter => {
    if (filter.type === FILTER_TYPES.AUTOCOMPLETE && !autocompleteRefs.current[filter.key]) {
      autocompleteRefs.current[filter.key] = createRef<AutocompleteSelectorRef>();
    }
  });

  const scrollableContentRef = useRef<HTMLDivElement>(null);

  // Makes sure all open autocomplete filters dropdowns are closed
  // on more filters containers scroll
  const handleScroll = useCallback(() => {
    Object.values(autocompleteRefs.current).forEach(ref => {
      if (ref.current?.isDropdownOpen) {
        ref.current?.closeDropdown();
      }
    });
  }, []);

  const addScrollListenerEvent = useCallback(() => {
    const el = scrollableContentRef.current;
    if (el) {
      el.addEventListener('scroll', handleScroll, { passive: true });
    }
  }, [handleScroll]);

  const removeScrollListenerEvent = useCallback(() => {
    const el = scrollableContentRef.current;
    if (el) {
      el.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  useEffect(() => {
    return () => {
      removeScrollListenerEvent();
    };
  }, [removeScrollListenerEvent]);

  return (
    <Popover
      open={open}
      onClose={(_event, reason) => {
        if (isInnerPopoverOpenRef.current && reason === 'backdropClick') return;
        onClose();
      }}
      anchorEl={anchorEl}
      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      slotProps={{
        transition: {
          onEntered: addScrollListenerEvent,
          onExited: removeScrollListenerEvent,
        },
      }}
    >
      <Box
        sx={{
          minWidth: 360,
          maxWidth: 480,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 408,
        }}
        data-test="more-filters-menu"
      >
        <Box
          sx={theme => ({
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            padding: theme.spacing(1, 2),
          })}
        >
          <Typography variant="h3">{t('More Filters')}</Typography>

          <IconButton
            aria-label="close button icon"
            data-test="close-button"
            size="large"
            sx={{ height: 28, padding: 0, width: 28 }}
            onClick={onClose}
          >
            <Close />
          </IconButton>
        </Box>

        <Stack
          direction="column"
          sx={{
            overflowY: 'auto',
            flexGrow: 1,
            maxHeight: '100%',
            scrollbarGutter: 'stable',
          }}
          data-test="more-filters-menu-content"
          ref={scrollableContentRef}
        >
          {filters.map(filter => {
            const { key, name, options, highlighted, applyImmediatly } = filter;

            return (
              <Box
                sx={theme => ({
                  borderTop: `1px solid ${theme.palette.divider}`,
                  backgroundColor: highlighted ? theme.palette.background.boxLight : 'transparent',
                  padding: `${theme.spacing(3)} ${theme.spacing(2)}`,

                  '&:first-of-type': {
                    paddingTop: theme.spacing(1.5),
                  },
                })}
                key={key}
                data-test={`filter-${name.replace(/\s/g, '-').toLowerCase()}-box`}
              >
                <Typography
                  variant="body1Bold"
                  data-test={'filter-name-label'}
                  data-test-2={`filter-name-label-${name.toLowerCase()}`}
                  sx={{
                    marginBottom: theme => theme.spacing(1.5),
                    color: theme => (highlighted ? theme.palette.text.secondary : theme.palette.text.primary),
                  }}
                >
                  {name}

                  {filter.subName && (
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 'normal',
                        fontSize: 'inherit',
                        marginLeft: 0.5,
                        color: theme => theme.palette.text.secondary,
                      }}
                    >
                      ({filter.subName})
                    </Typography>
                  )}
                </Typography>

                <FilterContentRenderer
                  options={options}
                  appliedFilters={criteria?.filters}
                  onOptionChange={applyImmediatly ? onFilterChangeImmediately : onFilterChange}
                  filter={filter}
                  isDisabled={disabledFilters?.includes(key)}
                  isLoading={loadingFilters?.includes(key)}
                  autocompleteRef={autocompleteRefs.current[key]}
                  setValidationState={setIsApplyButtonDisabled}
                  onInnerPopoverOpen={(open: boolean) => {
                    isInnerPopoverOpenRef.current = open;
                  }}
                />
              </Box>
            );
          })}
        </Stack>

        <Box
          sx={[
            {
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            },
            theme => ({
              padding: theme.spacing(1.5, 2),
              borderTop: `1px solid ${theme.palette.divider}`,
            }),
          ]}
        >
          <Button disabled={isApplyButtonDisabled} variant="contained" color="primary" onClick={onFilterApply}>
            {t('Apply')}
          </Button>

          <Button variant="text" onClick={onFilterClear} sx={{ color: 'textSecondary' }}>
            {t('Clear filters')}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};
